/** Canonical, bounded analysis state shared by Creative Performance URLs, queries, and controls. */
import { CREATIVE_METRIC_ENTRIES, isNumericCreativeMetric, type ICreativeNumericMetricKey } from 'shared'
import type { CreativePerformanceFieldsData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/creative-intelligence/creatives/fields'

import {
    CREATIVE_FILTER_FIELDS,
    type ICreativeFilterField,
    type ICreativeFilterState,
} from '@/features/creative-performance/creative-performance-filter-helpers'
import type {
    ICreativeGridSortBy,
    ICreativeGridSortDir,
} from '@/api/creative-intelligence/get-creative-performance-query'

export type ICreativePerformanceField = CreativePerformanceFieldsData[number]
export type ICreativePerformanceFieldId = ICreativePerformanceField['id']
export type ICreativePerformanceOperator = ICreativePerformanceField['operators'][number]
/**
 * Every metric id — DERIVED from the shared catalog, and the complement that defines the groupable
 * provider dimensions.
 *
 * This was the fifth hand-written list of metrics in the codebase, and the most dangerous, because
 * `ICreativePerformanceDimensionFieldId` is its COMPLEMENT: a metric the server published and this
 * list omitted did not merely go missing, it was silently reclassified as a groupable dimension and
 * indexed into `row.dimensions`, where it does not exist.
 *
 * A non-numeric metric (a status, a date) is excluded on the same rule the backend registry applies:
 * every metric operator is a comparison, so those stay dimensions.
 */
export const CREATIVE_PERFORMANCE_METRIC_FIELD_IDS = CREATIVE_METRIC_ENTRIES.filter(
    isNumericCreativeMetric
).map((metric) => metric.key) as readonly ICreativeNumericMetricKey[]

export type ICreativePerformanceMetricFieldId = (typeof CREATIVE_PERFORMANCE_METRIC_FIELD_IDS)[number]

export type ICreativePerformanceDimensionFieldId = Exclude<
    ICreativePerformanceFieldId,
    ICreativePerformanceMetricFieldId
>
export type ICreativePerformanceFilterMode = 'all' | 'any'
export type ICreativePerformanceGroupOrderField =
    | 'label'
    | 'creativeCount'
    | 'adCount'
    | 'spend'
    | 'roas'
    | 'purchaseValue'
    | 'cpa'
    | 'costPerResult'
    | 'impressions'
    | 'purchases'
    | 'results'

export interface ICreativePerformanceFilterRule {
    field: ICreativePerformanceFieldId
    operator: ICreativePerformanceOperator
    value?: string | number
    values?: string[]
    min?: number
    max?: number
}

/**
 * One condition group: its own predicates, and the operator joining them. Nesting is exactly two
 * levels — a group holds rules, never other groups.
 */
export interface ICreativePerformanceFilterGroup {
    mode: ICreativePerformanceFilterMode
    rules: ICreativePerformanceFilterRule[]
    /** Which toolbar pill owns this group. Omitted on legacy / simple single-group rows. */
    presentation?: 'simple' | 'advanced'
}

/**
 * The filter EXPRESSION: an ordered list of groups plus the operator joining them.
 *
 * The flat condition list this replaced is exactly the one-group case, whose join `mode` is inert.
 * That is what lets a single-group expression serialise to the identical bytes the flat form
 * produced, so existing links and saved views round-trip with no migration.
 */
export interface ICreativePerformanceFilterState {
    mode: ICreativePerformanceFilterMode
    groups: ICreativePerformanceFilterGroup[]
}

/** The unfiltered expression: one empty group, which is what an untouched filter row shows. */
export const EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE: ICreativePerformanceFilterState = {
    mode: 'all',
    groups: [{ mode: 'all', rules: [] }],
}

/** Total predicates in an expression — the quantity the rule cap actually bounds. */
export function creativePerformanceRuleCount(state: ICreativePerformanceFilterState) {
    return state.groups.reduce((sum, group) => sum + group.rules.length, 0)
}

/** Every predicate in the expression, group order preserved. For dimension collection and facets. */
export function creativePerformanceAllRules(state: ICreativePerformanceFilterState) {
    return state.groups.flatMap((group) => group.rules)
}

export interface ICreativePerformanceSortRule {
    field: ICreativePerformanceFieldId
    direction: ICreativeGridSortDir
}

/** Cap on rules SUMMED ACROSS every group — mirrors the API's cap on the whole expression. */
export const CREATIVE_PERFORMANCE_MAX_FILTER_RULES = 20
/** Cap on condition groups — mirrors the API's `CREATIVE_PERFORMANCE_MAX_FILTER_GROUPS`. */
export const CREATIVE_PERFORMANCE_MAX_FILTER_GROUPS = 5
export const CREATIVE_PERFORMANCE_MAX_FILTER_VALUES = 50
export const CREATIVE_PERFORMANCE_MAX_FILTER_VALUE_LENGTH = 256
export const CREATIVE_PERFORMANCE_MAX_FILTER_BYTES = 4_096
/**
 * The surface ranks by ONE field. A grid of creative cards is read one number at a time, so the
 * second and third rules an ordered sort allowed were invisible tie-breaks nobody set deliberately.
 *
 * `sorts` stays a LIST because that is the wire shape — `sorts=field:dir` is the same param a
 * one-rule sort always encoded to, so every stored view and pasted link round-trips without a
 * migration. The cap, not the type, is what makes a multi-rule ranking unrepresentable; nothing
 * latent survives behind it.
 */
export const CREATIVE_PERFORMANCE_MAX_SORTS = 1
/**
 * The surface groups by ONE property. The API still accepts up to three nested levels, but a grid
 * of cards — and a table read one band at a time — is read at a single grain, so the extra levels
 * were a depth nobody chose deliberately. Worse, they made the grouping unswappable: at the cap
 * every unselected property went inert, so picking a different one did nothing at all.
 *
 * `groupBy` stays a LIST because that is the wire shape — `group=field` is the same param a
 * one-level grouping always encoded to, so every stored view and pasted link round-trips without a
 * migration (a stored two-level link parses down to its outer level). The cap, not the type, is
 * what makes a nested grouping unrepresentable; nothing latent survives behind it.
 */
export const CREATIVE_PERFORMANCE_MAX_GROUPS = 1

/** Mirrors the server registry's dimension order, which is its category order. */
export const CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS: ICreativePerformanceDimensionFieldId[] = [
    'adAccount',
    'campaign',
    'adSet',
    'adName',
    'uniqueCreative',
    'name',
    'creativeType',
    'format',
    'videoLength',
    'primaryText',
    'headline',
    'cta',
    'landingPage',
    'landingPageDomain',
    'targetCountry',
    'placementTargeting',
    'ageRange',
    'genderTargeting',
    'languageTargeting',
    'deviceTargeting',
    'objective',
    'optimizationGoal',
    'budgetMode',
    'deliveryStatus',
    'launchDate',
    'launchWeek',
    'launchMonth',
]

export const CREATIVE_PERFORMANCE_FIELD_IDS: ICreativePerformanceFieldId[] = [
    ...CREATIVE_PERFORMANCE_METRIC_FIELD_IDS,
    ...CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS,
]

const OPERATORS: ICreativePerformanceOperator[] = [
    'gte',
    'lte',
    // Set-relative: the bound IS the window's median for the field, so they carry no value of their
    // own and are deliberately absent from the value-required checks below.
    'gteMedian',
    'lteMedian',
    // Set-relative WITH a value: the value is a house floor, and the bound applied is the lesser of
    // it and the Nth-ranked row's own value. It therefore behaves like `gte` for the value checks.
    'gteAdaptiveFloor',
    'between',
    'eq',
    'is',
    'isNot',
    'contains',
    'doesNotContain',
    'isAnyOf',
    'isNoneOf',
    'isSet',
    'isNotSet',
    'isMixed',
    'isNotMixed',
]

const LEGACY_TO_FIELD: Record<ICreativeFilterField, { field: ICreativePerformanceFieldId; operator: 'gte' | 'lte' }> = {
    minSpend: { field: 'spend', operator: 'gte' },
    maxSpend: { field: 'spend', operator: 'lte' },
    minRevenue: { field: 'purchaseValue', operator: 'gte' },
    maxRevenue: { field: 'purchaseValue', operator: 'lte' },
    minCpa: { field: 'cpa', operator: 'gte' },
    maxCpa: { field: 'cpa', operator: 'lte' },
    minRoas: { field: 'roas', operator: 'gte' },
    maxRoas: { field: 'roas', operator: 'lte' },
    minImpressions: { field: 'impressions', operator: 'gte' },
    maxImpressions: { field: 'impressions', operator: 'lte' },
}

const FIELD_TO_LEGACY = new Map(
    Object.entries(LEGACY_TO_FIELD).map(([key, value]) => [
        `${value.field}:${value.operator}`,
        key as ICreativeFilterField,
    ])
)

const LEGACY_SORT_TO_FIELD: Record<ICreativeGridSortBy, ICreativePerformanceFieldId> = {
    spend: 'spend',
    roas: 'roas',
    cpa: 'cpa',
    revenue: 'purchaseValue',
    impressions: 'impressions',
    name: 'name',
}

const FIELD_TO_LEGACY_SORT = new Map(
    Object.entries(LEGACY_SORT_TO_FIELD).map(([legacy, field]) => [field, legacy as ICreativeGridSortBy])
)

/**
 * Whether a string names a field the SERVER knows — the gate for anything that will be sent as a
 * filter or a sort.
 *
 * Exported because the metric catalog is deliberately wider than this list: a card may display a
 * metric (adds to cart, landing page views, three-second plays, delivery status) that the backend
 * offers no ordering for. Narrowing here is what stops the grid asking for a sort the server would
 * reject, without a cast that would merely hide the mismatch.
 */
export function isCreativePerformanceFieldId(value: unknown): value is ICreativePerformanceFieldId {
    return typeof value === 'string' && CREATIVE_PERFORMANCE_FIELD_IDS.includes(value as ICreativePerformanceFieldId)
}

const isField = isCreativePerformanceFieldId

function isOperator(value: unknown): value is ICreativePerformanceOperator {
    return typeof value === 'string' && OPERATORS.includes(value as ICreativePerformanceOperator)
}
/**
 * Fields whose bounds may be NEGATIVE — the period-over-period changes. Money, counts and rates
 * cannot fall below zero, and rejecting a negative there catches a mangled URL; a change legitimately
 * can ("CTR fell by at least 15%" is `<= -0.15`), and dropping it silently is how a filter quietly
 * stops filtering. Mirrors the API registry's `signed` flag.
 */
// DERIVED from the catalog, never hand-listed: a `signedRate` metric missing from this set has its
// negative bounds silently dropped on decode, and the API registry already derives `signed` the same
// way. Two hand-maintained mirrors of one fact is how a filter quietly stops filtering.
const SIGNED_FIELDS = new Set<string>(
    CREATIVE_METRIC_ENTRIES.filter((entry) => entry.unit === 'signedRate').map((entry) => entry.key)
)

function finiteBound(value: unknown, field: string): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false
    return SIGNED_FIELDS.has(field) ? true : value >= 0
}

function normalizeRule(value: unknown): ICreativePerformanceFilterRule | null {
    if (!value || typeof value !== 'object') return null
    const source = value as Record<string, unknown>
    if (!isField(source.field) || !isOperator(source.operator)) return null
    const rule: ICreativePerformanceFilterRule = { field: source.field, operator: source.operator }
    if (typeof source.value === 'string') {
        const text = source.value.trim()
        if (text.length > CREATIVE_PERFORMANCE_MAX_FILTER_VALUE_LENGTH) return null
        if (text) rule.value = text
    } else if (finiteBound(source.value, rule.field)) rule.value = source.value
    if (Array.isArray(source.values)) {
        if (source.values.length > CREATIVE_PERFORMANCE_MAX_FILTER_VALUES) return null
        if (
            source.values.some(
                (item) => typeof item !== 'string' || item.trim().length > CREATIVE_PERFORMANCE_MAX_FILTER_VALUE_LENGTH
            )
        ) {
            return null
        }
        rule.values = [
            ...new Set(
                source.values
                    .filter((item): item is string => typeof item === 'string')
                    .map((item) => item.trim())
                    .filter(Boolean)
            ),
        ].sort()
    }
    if (finiteBound(source.min, rule.field)) rule.min = source.min
    if (finiteBound(source.max, rule.field)) rule.max = source.max
    if (['gte', 'lte', 'eq'].includes(rule.operator) && typeof rule.value !== 'number') return null
    if (['is', 'isNot', 'contains', 'doesNotContain'].includes(rule.operator) && typeof rule.value !== 'string') {
        return null
    }
    if (['isAnyOf', 'isNoneOf'].includes(rule.operator) && !rule.values?.length) return null
    if (rule.operator === 'between' && (rule.min === undefined || rule.max === undefined || rule.min > rule.max)) {
        return null
    }
    return rule
}

function canonicalRule(rule: ICreativePerformanceFilterRule) {
    return JSON.stringify({
        field: rule.field,
        operator: rule.operator,
        ...(rule.value !== undefined ? { value: rule.value } : {}),
        ...(rule.values?.length ? { values: [...new Set(rule.values)].sort() } : {}),
        ...(rule.min !== undefined ? { min: rule.min } : {}),
        ...(rule.max !== undefined ? { max: rule.max } : {}),
    })
}

/** Normalize semantically unordered filter rules for stable URLs and query keys. */
export function normalizeCreativePerformanceRules(rules: ICreativePerformanceFilterRule[]) {
    return rules
        .map(normalizeRule)
        .filter((rule): rule is ICreativePerformanceFilterRule => rule !== null)
        .sort((a, b) => canonicalRule(a).localeCompare(canonicalRule(b)))
}

/**
 * Normalize an expression: canonicalise rules WITHIN each group, then drop groups left empty.
 *
 * Group order is deliberately NOT sorted. Rule order inside a group is meaningless — sorting it is
 * what keeps a URL stable when pills are re-added in a different order — but group order is
 * user-authored structure that the advanced row renders top to bottom, and canonicalising it would
 * make "add a group" silently reshuffle the row the user is looking at.
 *
 * Passing `keepEmptyGroups` retains empty groups, which the EDITOR needs: a group the user just
 * added has no conditions yet and must not vanish underneath them.
 */
export function normalizeCreativePerformanceFilterState(
    state: ICreativePerformanceFilterState,
    { keepEmptyGroups = false }: { keepEmptyGroups?: boolean } = {}
): ICreativePerformanceFilterState {
    const groups = state.groups
        .map((group) => ({
            mode: group.mode,
            rules: normalizeCreativePerformanceRules(group.rules),
            ...(group.presentation !== undefined ? { presentation: group.presentation } : {}),
        }))
        .filter((group) => keepEmptyGroups || group.rules.length > 0)
    return { mode: state.mode, groups }
}

/** Convert readable legacy bounds to equivalent registry predicates. */
export function legacyCreativeFiltersToRules(filters: ICreativeFilterState) {
    return CREATIVE_FILTER_FIELDS.flatMap((key) => {
        const value = filters[key]
        if (value === undefined) return []
        return [{ ...LEGACY_TO_FIELD[key], value }] satisfies ICreativePerformanceFilterRule[]
    })
}

/**
 * The effective expression the server should evaluate, legacy readable bounds included.
 *
 * The bounds are CONJUNCTIVE by definition ("a card appears only if it satisfies all of them"), so
 * they enter as their own `all` group joined by `all` — never appended to an existing group. Folding
 * a `minSpend` into a group whose operator is `any` would turn "spent at least 100" into "…or spent
 * at least 100", admitting exactly the creatives the bound exists to exclude.
 */
export function effectiveCreativePerformanceFilterState(
    filters: ICreativeFilterState,
    state: ICreativePerformanceFilterState,
    /**
     * The EDITOR passes `true`. A group the user has just added has no conditions yet, and dropping
     * it here would make "Add group" look like it did nothing — the row is rendered from this value.
     * The server payload keeps the default, since an empty group is inert for evaluation anyway.
     */
    { keepEmptyGroups = false }: { keepEmptyGroups?: boolean } = {}
): ICreativePerformanceFilterState {
    const legacyRules = legacyCreativeFiltersToRules(filters)
    const normalized = normalizeCreativePerformanceFilterState(state, { keepEmptyGroups })
    if (legacyRules.length === 0) return normalized
    // A single POPULATED `all` group can absorb the bounds without changing meaning, which keeps the
    // common case a one-group expression and therefore keeps its serialization byte-identical to the
    // flat form. Counted on populated groups so a just-added empty group does not push the bounds
    // into a third group they do not need. Any other shape gets the bounds as a dedicated group.
    const populated = normalized.groups.filter((group) => group.rules.length > 0)
    if (populated.length <= 1 && (populated[0]?.mode ?? 'all') === 'all') {
        const absorbed = normalizeCreativePerformanceRules([...legacyRules, ...(populated[0]?.rules ?? [])])
        // Absorb into the group that holds the conditions, else into the FIRST group. Prepending a
        // new group when nothing is populated would manufacture a second group out of an untouched
        // filter, which then reads as advanced mode the user never asked for.
        const targetIndex = Math.max(
            0,
            normalized.groups.findIndex((group) => group.rules.length > 0)
        )
        const groups = normalized.groups.length
            ? normalized.groups.map((group, index) =>
                  index === targetIndex
                      ? {
                            mode: group.mode,
                            rules: absorbed,
                            ...(group.presentation !== undefined ? { presentation: group.presentation } : {}),
                        }
                      : group
              )
            : [{ mode: 'all' as const, rules: absorbed }]
        return { mode: 'all', groups }
    }
    return {
        mode: 'all',
        groups: [{ mode: 'all', rules: normalizeCreativePerformanceRules(legacyRules) }, ...normalized.groups],
    }
}

/**
 * Move simple conjunctive metric bounds back into readable legacy URL fields.
 *
 * Lifting out is only sound when the WHOLE expression is one `all` group: there, a rule and a
 * readable bound mean the same thing. With more than one group, or a single `any` group, the bounds
 * are folded into the expression as their own `all` group instead and the readable params cleared —
 * see `effectiveCreativePerformanceFilterState`.
 */
export function canonicalizeCreativePerformanceFilters(
    filters: ICreativeFilterState,
    state: ICreativePerformanceFilterState
): { filters: ICreativeFilterState; state: ICreativePerformanceFilterState } {
    const normalized = normalizeCreativePerformanceFilterState(state, { keepEmptyGroups: true })
    const populated = normalized.groups.filter((group) => group.rules.length > 0)
    const isSingleConjunctiveGroup =
        normalized.groups.length <= 1 &&
        populated.length <= 1 &&
        (populated[0]?.mode ?? 'all') === 'all' &&
        populated[0]?.presentation !== 'advanced'
    if (!isSingleConjunctiveGroup) {
        return {
            filters: {},
            state: effectiveCreativePerformanceFilterState(filters, normalized, { keepEmptyGroups: true }),
        }
    }
    const nextFilters = { ...filters }
    const remainder: ICreativePerformanceFilterRule[] = []
    for (const rule of populated[0]?.rules ?? []) {
        const legacy = FIELD_TO_LEGACY.get(`${rule.field}:${rule.operator}`)
        if (legacy && typeof rule.value === 'number' && nextFilters[legacy] === undefined)
            nextFilters[legacy] = rule.value
        else remainder.push(rule)
    }
    // Keep the group shells the editor is holding (an empty group the user just added), but replace
    // the populated one's rules with whatever the readable params could not absorb.
    const groups = normalized.groups.map((group) =>
        group.rules.length > 0
            ? {
                  mode: group.mode,
                  rules: remainder,
                  ...(group.presentation !== undefined ? { presentation: group.presentation } : {}),
              }
            : group
    )
    return { filters: nextFilters, state: { mode: normalized.mode, groups } }
}

function encodeUtf8Base64Url(value: string) {
    const bytes = new TextEncoder().encode(value)
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

function decodeUtf8Base64Url(value: string) {
    if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error('invalid base64url')
    const binary = atob(value.replaceAll('-', '+').replaceAll('_', '/'))
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return new TextDecoder().decode(bytes)
}

function compactRule(rule: ICreativePerformanceFilterRule) {
    return {
        f: rule.field,
        o: rule.operator,
        ...(rule.value !== undefined ? { v: rule.value } : {}),
        ...(rule.values?.length ? { vs: rule.values } : {}),
        ...(rule.min !== undefined ? { n: rule.min } : {}),
        ...(rule.max !== undefined ? { x: rule.max } : {}),
    }
}

function compactCreativePerformanceFilters(state: ICreativePerformanceFilterState) {
    const allRules = creativePerformanceAllRules(state)
    if (state.groups.length > CREATIVE_PERFORMANCE_MAX_FILTER_GROUPS) {
        throw new Error(`At most ${CREATIVE_PERFORMANCE_MAX_FILTER_GROUPS} filter groups are allowed`)
    }
    if (allRules.length > CREATIVE_PERFORMANCE_MAX_FILTER_RULES) throw new Error('At most 20 filters are allowed')
    if (allRules.some((rule) => (rule.values?.length ?? 0) > CREATIVE_PERFORMANCE_MAX_FILTER_VALUES)) {
        throw new Error('At most 50 values are allowed per filter')
    }
    if (
        allRules.some(
            (rule) =>
                (typeof rule.value === 'string' && rule.value.length > CREATIVE_PERFORMANCE_MAX_FILTER_VALUE_LENGTH) ||
                rule.values?.some((value) => value.length > CREATIVE_PERFORMANCE_MAX_FILTER_VALUE_LENGTH)
        )
    ) {
        throw new Error('Filter values may contain at most 256 characters')
    }
    // Empty groups are KEPT here. They are inert for evaluation, but they are authored structure:
    // the applied row derives its advanced presentation from group count, and URL state is the only
    // place that structure lives. Dropping them would delete a group the user just added on the very
    // next render.
    const normalized = normalizeCreativePerformanceFilterState(state, { keepEmptyGroups: true })
    if (creativePerformanceAllRules(normalized).length !== allRules.length) throw new Error('Invalid filter rule')

    // A legacy or explicitly Simple singleton serialises to the flat `{m, r}` payload the
    // pre-grouping form produced, byte for byte, and its `m` is that group's OWN operator — which is
    // what an old payload's `m` has always meant. Advanced presentation has no equivalent in that
    // flat shape, so even a singleton Advanced group takes the grouped form to preserve its `p: 'a'`.
    const compact =
        normalized.groups.length > 1 || normalized.groups[0]?.presentation === 'advanced'
            ? JSON.stringify({
                  m: normalized.mode === 'any' ? 'o' : 'a',
                  g: normalized.groups.map((group) => ({
                      m: group.mode === 'any' ? 'o' : 'a',
                      r: group.rules.map(compactRule),
                      ...(group.presentation === 'simple'
                          ? { p: 's' }
                          : group.presentation === 'advanced'
                            ? { p: 'a' }
                            : {}),
                  })),
              })
            : JSON.stringify({
                  m: (normalized.groups[0]?.mode ?? 'all') === 'any' ? 'o' : 'a',
                  r: (normalized.groups[0]?.rules ?? []).map(compactRule),
              })
    if (new TextEncoder().encode(compact).byteLength > CREATIVE_PERFORMANCE_MAX_FILTER_BYTES) {
        throw new Error('Filters exceed 4 KB')
    }
    return { normalized, compact }
}

/** Reject an oversized/invalid state before it can be written to a URL or saved view. */
export function assertCreativePerformanceFilterLimits(state: ICreativePerformanceFilterState) {
    compactCreativePerformanceFilters(state)
}

/** Encode compact registry filter state, enforcing the same limits as the API. */
export function encodeCreativePerformanceFilters(state: ICreativePerformanceFilterState) {
    const { normalized, compact } = compactCreativePerformanceFilters(state)
    // A lone empty ADVANCED group is NOT unfiltered — it is the builder shell the user just
    // opened, and the URL is the only place that structure lives. Encoding it to '' collapsed the
    // committed state back to the default on the next round-trip, which reset the transient
    // advanced presentation and closed the construction popover underneath the user (deleting the
    // second empty group was the visible symptom). Same rule as the Research encoder.
    const isUnfiltered =
        normalized.groups.length === 0 ||
        (normalized.groups.length === 1 &&
            normalized.groups[0].rules.length === 0 &&
            normalized.groups[0].mode === 'all' &&
            normalized.groups[0].presentation !== 'advanced')
    if (isUnfiltered) return ''
    return encodeUtf8Base64Url(compact)
}

function decodeCompactRules(value: unknown) {
    const rawRules = Array.isArray(value) ? value : []
    if (rawRules.length > CREATIVE_PERFORMANCE_MAX_FILTER_RULES) throw new Error('too many rules')
    return normalizeCreativePerformanceRules(
        rawRules.flatMap((entry) => {
            if (!entry || typeof entry !== 'object') return []
            const compact = entry as Record<string, unknown>
            const rule = normalizeRule({
                field: compact.f,
                operator: compact.o,
                value: compact.v,
                values: compact.vs,
                min: compact.n,
                max: compact.x,
            })
            return rule ? [rule] : []
        })
    )
}

/** Tolerantly decode compact filters in either form, dropping malformed rules independently. */
export function decodeCreativePerformanceFilters(raw: string | null | undefined): ICreativePerformanceFilterState {
    if (!raw) return EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE
    try {
        const decoded = decodeUtf8Base64Url(raw)
        if (new TextEncoder().encode(decoded).byteLength > CREATIVE_PERFORMANCE_MAX_FILTER_BYTES) throw new Error()
        const source = JSON.parse(decoded) as Record<string, unknown>
        const mode = source.m === 'o' ? ('any' as const) : ('all' as const)
        if (Array.isArray(source.g)) {
            if (source.g.length > CREATIVE_PERFORMANCE_MAX_FILTER_GROUPS) throw new Error()
            const groups = source.g.flatMap((entry) => {
                if (!entry || typeof entry !== 'object') return []
                const group = entry as Record<string, unknown>
                return [
                    {
                        mode: group.m === 'o' ? ('any' as const) : ('all' as const),
                        rules: decodeCompactRules(group.r),
                        ...(group.p === 's'
                            ? { presentation: 'simple' as const }
                            : group.p === 'a'
                              ? { presentation: 'advanced' as const }
                              : {}),
                    },
                ]
            })
            const state = { mode, groups }
            if (creativePerformanceRuleCount(state) > CREATIVE_PERFORMANCE_MAX_FILTER_RULES) throw new Error()
            return state
        }
        // The flat form: `m` is the single group's OWN operator, not a join.
        return { mode, groups: [{ mode, rules: decodeCompactRules(source.r) }] }
    } catch {
        return EMPTY_CREATIVE_PERFORMANCE_FILTER_STATE
    }
}

/**
 * Read the single sort a `sorts=` param carries.
 *
 * A list carrying MORE than one rule — a link shared before the ranking became single-criterion, or
 * a saved view stored then — resolves to its FIRST rule and drops the remainder. That rule is the
 * one that was determining the visible order, so honouring it is what makes the old link still show
 * the buyer what they meant; clearing the whole list (the previous over-the-cap behaviour) would
 * silently re-rank their view by the default instead.
 *
 * A malformed LEAD token still yields nothing, so the surface falls back to the legacy aliases
 * rather than ranking on a rule the URL did not lead with.
 */
export function parseCreativePerformanceSorts(raw: string | null | undefined) {
    if (!raw) return [] as ICreativePerformanceSortRule[]
    const [field, direction, extra] = raw.split(',')[0].split(':')
    if (extra || !isField(field) || (direction !== 'asc' && direction !== 'desc')) {
        return [] as ICreativePerformanceSortRule[]
    }
    return [{ field, direction }] satisfies ICreativePerformanceSortRule[]
}

/** Encode the sort readably. */
export function encodeCreativePerformanceSorts(sorts: ICreativePerformanceSortRule[]) {
    if (sorts.length > CREATIVE_PERFORMANCE_MAX_SORTS) throw new Error('At most one sort is allowed')
    return sorts
        .slice(0, CREATIVE_PERFORMANCE_MAX_SORTS)
        .map((sort) => `${sort.field}:${sort.direction}`)
        .join(',')
}

/** Map a legacy single sort to its registry rule. */
export function legacyCreativeSort(sortBy: ICreativeGridSortBy, sortDir: ICreativeGridSortDir) {
    return { field: LEGACY_SORT_TO_FIELD[sortBy], direction: sortDir } satisfies ICreativePerformanceSortRule
}

/** Ordered sorts the server should apply. */
export function effectiveCreativePerformanceSorts(
    sortBy: ICreativeGridSortBy,
    sortDir: ICreativeGridSortDir,
    sorts: ICreativePerformanceSortRule[]
) {
    return sorts.length ? sorts : [legacyCreativeSort(sortBy, sortDir)]
}

/** Collapse a one-rule legacy-compatible sort back into the original readable URL aliases. */
export function canonicalizeCreativePerformanceSorts(
    sortBy: ICreativeGridSortBy,
    sortDir: ICreativeGridSortDir,
    sorts: ICreativePerformanceSortRule[]
) {
    if (sorts.length !== 1) return { sortBy, sortDir, sorts }
    const legacy = FIELD_TO_LEGACY_SORT.get(sorts[0].field)
    return legacy ? { sortBy: legacy, sortDir: sorts[0].direction, sorts: [] } : { sortBy, sortDir, sorts }
}

/** Requested provider payload is the union of visible and analytical dimension dependencies. */
export function creativePerformanceIncludeDimensions(
    columns: readonly string[],
    /** Every predicate in the filter EXPRESSION, whichever group it sits in. */
    rules: ICreativePerformanceFilterRule[],
    sorts: ICreativePerformanceSortRule[],
    /** Every selected group LEVEL, so a nested grouping resolves each of its dimensions. */
    groups: readonly ICreativePerformanceDimensionFieldId[]
) {
    const dimensions = new Set<ICreativePerformanceDimensionFieldId>()
    const add = (field: string) => {
        if (CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS.includes(field as ICreativePerformanceDimensionFieldId)) {
            dimensions.add(field as ICreativePerformanceDimensionFieldId)
        }
    }
    columns.forEach(add)
    rules.forEach((rule) => add(rule.field))
    sorts.forEach((sort) => add(sort.field))
    groups.forEach(add)
    return [...dimensions].sort()
}

export const CREATIVE_PERFORMANCE_OPERATOR_LABELS: Record<ICreativePerformanceOperator, string> = {
    gte: 'is at least',
    lte: 'is at most',
    // Named for what they MEAN to a buyer. "is above the median" reads as a comparison against the
    // account's own typical value, which is the whole point of a set-relative bound.
    gteMedian: 'is at or above the median',
    lteMedian: 'is at or below the median',
    // Names the INTENT, not the arithmetic. The number shown is the house floor; the floor actually
    // applied is disclosed by the view when the account's volume forced it lower.
    gteAdaptiveFloor: 'is at least (or the top 10)',
    between: 'is between',
    eq: 'equals',
    is: 'is',
    isNot: 'is not',
    contains: 'contains',
    doesNotContain: 'does not contain',
    isAnyOf: 'is any of',
    isNoneOf: 'is none of',
    isSet: 'is set',
    isNotSet: 'is not set',
    isMixed: 'is mixed',
    isNotMixed: 'is not mixed',
}
